import type { AxePurchaseState } from '@deque/billing-service-client';
import {
  getAllUserSubscriptions,
  listProductAccess,
  type ProductSlugs
} from '@deque/billing-utils';
import { Loader } from '@deque/cauldron-react';
import React, { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Redirect } from 'react-router-dom';

import { useAuthContext } from '../contexts/auth';
import { useGlobalToast } from '../contexts/globalToast';
import { useOnPremValue } from '../contexts/onPrem';
import { useProducts } from '../contexts/products';
import { useServerInfo } from '../contexts/serverInfo';

export interface SubscriptionRequiredProps {
  productSlug: ProductSlugs | ProductSlugs[];
  allowedStates?: AxePurchaseState[];
  redirectTo?: string;
  nonSelfProvisioningRedirectTo?: string;
  /** Custom error message to display when access is denied */
  customErrorMessage?: string;
  children: React.ReactNode;
}

const SubscriptionRequired: React.FC<SubscriptionRequiredProps> = ({
  productSlug,
  allowedStates = ['paid', 'trialing'],
  redirectTo = '/',
  nonSelfProvisioningRedirectTo = '/',
  customErrorMessage,
  children
}: SubscriptionRequiredProps) => {
  const { t } = useTranslation();

  const isOnPrem = useOnPremValue();
  const { loading: authLoading, billingUser } = useAuthContext();
  const { loading: productsLoading, getProductBySlug } = useProducts();
  const { loading: serverInfoLoading, serverInfo } = useServerInfo();
  const { setContents } = useGlobalToast();

  const productSlugsArray = useMemo(
    () => (Array.isArray(productSlug) ? productSlug : [productSlug]),
    [productSlug]
  );

  const products = useMemo(
    () => productSlugsArray.map(getProductBySlug),
    [productSlugsArray, getProductBySlug]
  );

  const productAccessList = useMemo(() => {
    if (!billingUser) {
      return;
    }

    const subscriptions = getAllUserSubscriptions(billingUser, 'active-only');
    const allAccess = listProductAccess(subscriptions);
    return productSlugsArray.map(slug => allAccess[slug] || null);
  }, [billingUser, productSlug]);

  useEffect(() => {
    if (!productAccessList?.length || !products.length) {
      return;
    }

    const hasAccess = productAccessList.some(
      access => access && allowedStates.includes(access)
    );

    if (!hasAccess) {
      setContents(
        customErrorMessage ??
          t('You do not have permission to access {{ productName }}', {
            productName: products.map(product => product?.name).join(', ')
          }),
        'caution'
      );
    }
  }, [productAccessList, products]);

  let redirectPath = redirectTo;
  if (serverInfo?.isSelfProvisioningEnabled === false) {
    redirectPath = nonSelfProvisioningRedirectTo;
  }

  if (isOnPrem) {
    return <>{children}</>;
  }

  if (authLoading || productsLoading || serverInfoLoading) {
    return <Loader />;
  }

  if (!billingUser || !products.length) {
    return <Redirect to={redirectPath} />;
  }

  const isSubscribed = productAccessList?.some(
    access => access && allowedStates.includes(access)
  );

  if (!isSubscribed) {
    return <Redirect to={redirectPath} />;
  }

  return <>{children}</>;
};

export default SubscriptionRequired;
